// Phone-book import. Uses the native Capacitor Contacts plugin inside the APK,
// and falls back to the browser Contact Picker API (Chrome on Android, HTTPS)
// on the web. Returns { name, mobile } for a single picked contact, or null.
import { Capacitor } from '@capacitor/core';

const tenDigit = (s) => {
  const d = String(s || '').replace(/\D/g, '');
  return d.length > 10 ? d.slice(-10) : d;
};
// Indian mobile = 10 digits starting 6-9. Landlines / short codes are not invitable.
const isMobile = (d) => /^[6-9]\d{9}$/.test(d);
// A contact can hold several numbers (home, work, 2 SIMs): prefer the first real
// mobile, else the first number at all.
const bestMobile = (phones) => {
  const nums = (phones || []).map(p => tenDigit(p && p.number)).filter(Boolean);
  return nums.find(isMobile) || nums[0] || '';
};

// Is a single-contact picker available? (used to show/hide the button)
export const contactsSupported = () =>
  Capacitor.isNativePlatform() ||
  (typeof navigator !== 'undefined' && !!navigator.contacts && !!navigator.contacts.select);

// Bulk import (read the whole phone book) is native-only — the web Contact
// Picker can't enumerate all contacts.
export const bulkContactsSupported = () => Capacitor.isNativePlatform();

// Read the entire phone book → [{ surname, name, fatherHusband, mobile, display }],
// deduped by mobile, sorted by display name. Native APK only.
export async function getDeviceContacts() {
  if (!Capacitor.isNativePlatform()) throw new Error('Bulk contact import works in the mobile app');
  const { Contacts } = await import('@capacitor-community/contacts');
  const perm = await Contacts.requestPermissions();
  if (perm.contacts !== 'granted') throw new Error('Contacts permission denied');
  const res = await Contacts.getContacts({ projection: { name: true, phones: true } });
  const list = (res && res.contacts) || [];
  const out = [];
  const seen = new Set();
  for (const c of list) {
    const nm = c.name || {};
    const mobile = bestMobile(c.phones);
    const name = nm.given || nm.display || '';
    const surname = nm.family || '';
    const display = [surname, name].filter(Boolean).join(' ') || nm.display || '(no name)';
    if (!mobile && display === '(no name)') continue;
    const key = mobile || `n:${display}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ surname, name, fatherHusband: nm.middle || '', mobile, display, key });
  }
  out.sort((a, b) => a.display.localeCompare(b.display));
  return out;
}

// Returns { surname, name, fatherHusband, mobile } for one picked contact.
export async function pickContactFromDevice() {
  // ── Native APK: system contact picker via @capacitor-community/contacts ──
  if (Capacitor.isNativePlatform()) {
    const { Contacts } = await import('@capacitor-community/contacts');
    const perm = await Contacts.requestPermissions();
    if (perm.contacts !== 'granted') throw new Error('Contacts permission denied');
    const res = await Contacts.pickContact({ projection: { name: true, phones: true } });
    const contact = res && res.contact;
    if (!contact) return null;
    const nm = contact.name || {};
    // given → Name, family → Surname, middle → Father/Husband (best effort).
    const name = nm.given || nm.display || '';
    return {
      surname: nm.family || '',
      name,
      fatherHusband: nm.middle || '',
      mobile: bestMobile(contact.phones),
    };
  }

  // ── Web: Contact Picker API (Chrome/Android over HTTPS, needs a user gesture) ──
  if (typeof navigator !== 'undefined' && navigator.contacts && navigator.contacts.select) {
    const picked = await navigator.contacts.select(['name', 'tel'], { multiple: false });
    if (!picked || !picked.length) return null;
    const c = picked[0];
    // The web API only gives a single display name — put it in Name.
    return {
      surname: '',
      name: (c.name && c.name[0]) || '',
      fatherHusband: '',
      mobile: tenDigit(c.tel && c.tel[0]),
    };
  }

  throw new Error('Contact picker is not supported on this device');
}
